import { Component, ElementRef, Input, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs/internal/Subscription';
import { CommonBroadcastService } from '../common-broadcast/common-broadcast.service';
import { IDictionary } from '../dictionary/IDictionary';
import { HighchartsService } from './highcharts.service';
import * as Highcharts from 'highcharts';
import * as _ from 'lodash';
import { DateRange, EventTypes } from '../../src/typings/models';
import { ThemeService } from '../theme/ThemeService';

interface Dimensions {
    width: number,
    height: number
}

@Component({
    selector: 'cl-highcharts',
    template: ``,
    styleUrls: ['./highcharts.component.scss']
})
export class HighchartsComponent implements OnInit, OnDestroy {
    public static $name = 'clHighcharts';

    @Input() config: Highcharts.Options;
    private axisNames: string[] = ['xAxis', 'yAxis'];

    private chartTypeMap: IDictionary<string> = {
        chart: 'Chart',
        map: 'Map',
        stock: 'StockChart'
    };

    private MAX_DATA_POINTS = 500;

    // acceptable shared state
    private chart: Highcharts.Chart;
    private prevSeriesOptions: any = {};
    private seriesId: number = 0;
    private subscriptions: Subscription;
    private dimensions: Dimensions;

    constructor(
        private elRef: ElementRef,
        private highchartsService: HighchartsService,
        private commonBroadcastService: CommonBroadcastService,
        private themeService: ThemeService,
    ) {

    }

    ngOnDestroy() {
        if (this.chart) {
            try {
                this.chart.destroy();
            } catch (ex) {
                // fail silently as highcharts will throw exception if element doesn't exist
            }
        }
        this.subscriptions?.unsubscribe();
    }

    ngOnInit() {
        this.initChart();
        this.subscribeToChartEvents();
    }

    isDarkTheme() {
        return this.themeService.getTheme() === 'casDark';
    }

    private initChart() {
        if (this.chart) {
            this.chart.destroy();
        }

        const config = this.config || {};
        const mergedOptions = this.getMergedOptions(this.elRef.nativeElement, config);
        const chartType = this.getChartType(config);

        this.chart = new Highcharts[chartType](mergedOptions);

        // tslint:disable-next-line: prefer-for-of
        for (let i = 0; i < this.axisNames.length; i++) {
            let currentAxisName = this.axisNames[i];

            if (config[currentAxisName]) {
                this.processExtremes(this.chart, config[currentAxisName], currentAxisName);
            }
        }
    }

    private getMergedOptions(element, config) {
        const defaultOptions = this.getDefaultOptions();
        const mergedOptions: any = this.highchartsService.deepExtend(defaultOptions, config || {});

        mergedOptions.chart.renderTo = element;
        return mergedOptions;
    }

    private getDefaultOptions(): Highcharts.Options {
        // The chart should be styled to match the rest of the theme
        const bgColor = this.isDarkTheme() ? "rgba(48, 48, 48, 1)" : "rgba(250, 250, 250, 1)";
        const contrastColor = this.isDarkTheme() ? 'rgba(255,255,255,0.87)' : "rgba(0,0,0,0.87)";

        const defaultOptions: Highcharts.Options = {};

        defaultOptions.chart = {
            backgroundColor: bgColor,
            events: {},
            style: {
                fontFamily: `'Roboto', 'Arial', sans-serif`
            }
        };

        defaultOptions.credits = {
            enabled: false
        };

        defaultOptions.legend = {
            itemHoverStyle: {
                color: contrastColor
            },
            itemStyle: {
                color: contrastColor
            },
            useHTML: true
        };

        defaultOptions.noData = {
            useHTML: true
        };

        defaultOptions.plotOptions = {
            series: {
                events: {
                    checkboxClick(event: any) {
                        // The default behavior of the checkbox is to remove the item from the series -- we should only hide it, per #2249
                        event.preventDefault();
                        event.item.setVisible();
                    },
                    legendItemClick(event: any) {
                        // Clicking the legend should also check or uncheck the checkbox
                        event.target.checkbox.checked = !event.target.checkbox.checked;
                    }
                },
                showCheckbox: true
            }
        };

        defaultOptions.series = [];

        defaultOptions.subtitle = {
            style: {
                color: contrastColor
            }
        };

        defaultOptions.title = {
            margin: 15,
            style: {
                color: contrastColor
            }
        };

        defaultOptions.xAxis = {
            events: {},
            labels: {
                style: {
                    color: contrastColor
                }
            }
        };

        defaultOptions.yAxis = {
            events: {}
        };

        return defaultOptions;
    }

    private getChartType(config) {
        if (config === undefined) { return 'Chart'; }

        const mappedType = this.chartTypeMap[('' + config.chartType).toLowerCase()];

        return mappedType || (config.useHighStocks ? 'StockChart' : 'Chart');
    }

    private processExtremes(chart, axis, axisName) {
        if (axis.currentMin || axis.currentMax) {
            chart[axisName][0].setExtremes(axis.currentMin, axis.currentMax, true);
        }
    }

    private subscribeToChartEvents() {
        this.subscriptions = new Subscription();

        const refreshSubscription = this.commonBroadcastService.on(EventTypes.IssueRefreshEvent)
            .subscribe(() => {
                this.chart.redraw();
            });

        const zoomOutSubscription = this.commonBroadcastService.on(EventTypes.HighchartsZoomOut)
            .subscribe(() => {
                this.chart.zoomOut();
            });

        const zoomOutOneLevelSubscription = this.commonBroadcastService.on<DateRange>(EventTypes.HighchartsZoomOutOneLevel)
            .subscribe((range) => {
                this.chart.xAxis.forEach((x) => {
                    range ? x.setExtremes(range.start, range.end, false) : x.setExtremes(undefined, undefined, false);
                });
                this.chart.redraw();
            });

        const addPlotLineXSubscription = this.commonBroadcastService.on<Highcharts.AxisPlotLinesOptions>(EventTypes.HighchartsAddPlotLineX)
            .subscribe(option => {
                this.chart.xAxis[0].addPlotLine(option);
            });

        const removePlotLineXSubscription = this.commonBroadcastService.on<string>(EventTypes.HighchartsRemovePlotLineX)
            .subscribe(id => this.chart.xAxis[0].removePlotLine(id));
            
        const seriesChangeSubscription = this.commonBroadcastService.on(EventTypes.SeriesChange).subscribe(() => {
            this.rerender()
        });


        const bulkSeriesUpdateSubscription = this.commonBroadcastService.on<Highcharts.BulkSeriesUpdate<any>>(EventTypes.BulkSeriesUpdate)
            .subscribe(bulkSeriesUpdate => {
                if (bulkSeriesUpdate?.series) {
                    bulkSeriesUpdate.series.forEach(s => {
                        this.updateSeries(s, bulkSeriesUpdate.setData, bulkSeriesUpdate.isLive)
                    })
                }
            });

        const plotOptionsChangeSubscription = this.commonBroadcastService.on(EventTypes.PlotOptionsChange)
            .subscribe(() => {
                this.rerender();
            });

        const chartDimensionsChangeSubscription = this.commonBroadcastService.on<Dimensions>(EventTypes.ChartDimensionsChange)
            .subscribe((newSize) => {
                if (this.dimensions?.height === newSize?.height && this.dimensions?.width === newSize?.width) { return; }
                this.dimensions = newSize;
                this.chart.setSize(newSize.width || this.chart.chartWidth, newSize.height || this.chart.chartHeight);
            });


        this.subscriptions.add(seriesChangeSubscription);
        this.subscriptions.add(bulkSeriesUpdateSubscription);
        this.subscriptions.add(plotOptionsChangeSubscription);
        this.subscriptions.add(chartDimensionsChangeSubscription);
        this.subscriptions.add(refreshSubscription);
        this.subscriptions.add(zoomOutSubscription);
        this.subscriptions.add(zoomOutOneLevelSubscription);
        this.subscriptions.add(addPlotLineXSubscription);
        this.subscriptions.add(removePlotLineXSubscription);
    }

    private rerender() {
        this.processSeries(this.config.series);
        this.chart.redraw();
    }

    private processSeries(series?: any[]) {
        const ids: string[] = [];

        if (series) {
            this.configureAxes(this.config, this.chart);

            this.ensureIds(series);

            // Find series to add or update
            series.forEach((s) => {
                ids.push(s.id);
                const chartSeries = this.chart.get(s.id) as Highcharts.Series;

                if (chartSeries) {
                    if (!_.isEqual(this.prevSeriesOptions[s.id], this.chartOptionsWithoutEasyOptions(s))) {
                        chartSeries.update(_.cloneDeep(s), false);
                    } else {
                        if (s.visible !== undefined && chartSeries.visible !== s.visible) {
                            chartSeries.setVisible(s.visible, false);
                        }

                        chartSeries.setData(s.data);
                    }
                } else {
                    this.chart.addSeries(s);
                }

                this.prevSeriesOptions[s.id] = this.chartOptionsWithoutEasyOptions(s);
            });
        }

        this.removeMissingSeries(this.chart.series, ids);
    }

    private configureAxes(config: Highcharts.Options, chart: Highcharts.Chart) {
        this.configureYAxes(config, chart);
        this.configureXAxis(config, chart);
    }

    private configureXAxis(config: Highcharts.Options, chart: Highcharts.Chart) {
        const xAxisOptions: Highcharts.AxisOptions = config.xAxis as Highcharts.AxisOptions;

        if (xAxisOptions && chart.xAxis.length === 0) {
            chart.addAxis(xAxisOptions);
        }
    }

    private configureYAxes(config: Highcharts.Options, chart: Highcharts.Chart) {
        const yAxisOptions: Highcharts.AxisOptions[] = config.yAxis as Highcharts.AxisOptions[];

        for (let axisIndex = 0; axisIndex < yAxisOptions.length; axisIndex++) {
            const axisOptions: Highcharts.AxisOptions = yAxisOptions[axisIndex];

            if (axisIndex < chart.yAxis.length) {
                chart.yAxis[axisIndex].update(axisOptions, false);
                this.updateZoom(chart.yAxis[axisIndex], _.cloneDeep(axisOptions));
            } else {
                chart.addAxis(axisOptions);
            }
        }
    }

    // Enclosed methods called from the directive during linking or during
    // watches for scope changes.
    private updateZoom(axis, modelAxis) {
        const extremes = axis.getExtremes();

        if (modelAxis.currentMin !== extremes.dataMin || modelAxis.currentMax !== extremes.dataMax) {
            if (axis.setExtremes) {
                axis.setExtremes(modelAxis.currentMin, modelAxis.currentMax, false);
            } else {
                axis.detachedsetExtremes(modelAxis.currentMin, modelAxis.currentMax, false);
            }
        }
    }

    /**
     * Define an id for each member of a series, if it doesn't already exist
     * @param series The series array -- this may get mutated
     */
    private ensureIds(series: any[]) {
        series.forEach((s) => {
            if (s.id === null || typeof s.id === 'undefined') {
                s.id = `series-${this.seriesId++}`;
            }
        });
    }

    private chartOptionsWithoutEasyOptions(options) {
        return Object.assign(
            this.highchartsService.deepExtend({}, options),
            { data: null, visible: null }
        );
    }

    private removeMissingSeries(series, ids) {
        for (let i = series.length - 1; i >= 0; i--) {
            const item = series[i];

            if (item.options.id !== 'highcharts-navigator-series' && ids.indexOf(item.options.id) < 0) {
                item.remove(false);
            }
        }
    }

    private updateSeries(series: any, setData: boolean, isLive: boolean) {
        const chartSeries: Highcharts.Series = this.chart.get(series.id) as Highcharts.Series;
        const data = series.data;
        let shiftPoints = false;

        if (isLive || chartSeries.data.length > this.MAX_DATA_POINTS) {
            shiftPoints = true;
        }

        /**
         * We need to use this method for zooming in,
         * otherwise we will begin to get "no data" after a few zoom levels and butchered data when resetting back out.
         * We also need to use this method for setting date ranges,
         * otherwise when setting a date range outside of the current one, then resetting back to the original,
         * the date range will remain the larger range.
         */
        if (setData) {
            chartSeries.setData(data, true, false, false);
        } else {
            // update the chart...
            // tslint:disable-next-line: one-variable-per-declaration
            for (let i = 0, total = data.length; i < total; i++) {
                chartSeries.addPoint(data[i], false, shiftPoints, true);
            }

            this.chart.redraw();
        }
    }
}